'server-only'

import { Stripe } from 'stripe'
import { createAdminClient } from '@/lib/supabase/admin'
import { logErrorAsAdmin } from './admin-supabase-query'

export type Customer = {
    id: number
    created_at: string
    updated_at: string
    user_id: string
    customer_id: string
    subscription_id: string
    plan_id: string
    status: 'active' | 'canceled' | 'past_due' | 'incomplete' | 'incomplete_expired' | 'trialing' | 'unpaid'
    current_period_start: string
    current_period_end: string
    cancel_at_period: string
    canceled_at: string
    metadata: Record<string, any> | null
}

export type Payment = {
    id: number
    created_at: string
    customer_id: string
    subscription_id: string
    payment_intent_id: string
    invoice_id: string
    amount: number
    currency: string
    status: string
    payment_method: string
    receipt_url: string
    metadata: Record<string, any>
    user_id: string
}

// Get subscription for a user
export async function getUserSubscription(userId: string): Promise<Customer | null> {
    console.log(`[getUserSubscription] Getting subscription for user ${userId}`)
    const supabase = await createAdminClient()
    const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('user_id', userId)
        .single()
    
    if (error) {
        console.error('Error fetching user subscription:', error)
        return null
    }
    
    return data
}

// Create or update a subscription
export async function upsertSubscription(subscription: Partial<Customer>): Promise<Customer | null> {
    console.log(`[upsertSubscription] Upserting subscription ${subscription.subscription_id}`)
    const supabase = await createAdminClient()
    const { data, error } = await supabase
        .from('customers')
        .upsert(subscription)
        .select('*')
        .single()
    
    if (error) {
        console.error('Error upserting subscription:', error)
        return null
    }
    
    return data
}

// Update subscription status
export async function updateSubscriptionStatus(
    subscriptionId: string, 
    status: Customer['status']
): Promise<boolean> {
    console.log(`[updateSubscriptionStatus] Updating subscription ${subscriptionId} to status ${status}`)
    const supabase = await createAdminClient()
    const { error } = await supabase
        .from('subscriptions')
        .update({ 
            status, 
            updated_at: new Date().toISOString() 
        })
        .eq('subscription_id', subscriptionId)
    
    if (error) {
        console.error('Error updating subscription status:', error)
        return false
    }
    
    return true
}

// Delete subscription
export async function deleteSubscription(subscriptionId: string): Promise<boolean> {
    console.log(`[deleteSubscription] Deleting subscription ${subscriptionId}`)
    const supabase = await createAdminClient()
    const { error } = await supabase
        .from('customers')
        .delete()
        .eq('subscription_id', subscriptionId)
    
    if (error) {
        console.error('Error deleting subscription:', error)
        return false
    }
    
    return true
}

// Add payment to history
export async function addPaymentRecord(payment: Omit<Payment, 'id' | 'created_at'>): Promise<Payment | null> {
    console.log(`[addPaymentRecord] Adding payment record for customer ${payment.customer_id}`)
    const supabase = await createAdminClient()
    const { data, error } = await supabase
        .from('payments')
        .insert(payment)
        .select('*')
        .single()
    
    if (error) {
        console.error('Error adding payment record:', error)
        return null
    }
    
    return data
}


/*
    // Start of Selection
    // Log Stripe event
*/
export async function logStripeEvent(
    eventId: string,
    type: string, 
    data: Stripe.Event,
    status: 'pending' | 'processing' | 'processed' | 'failed' = 'pending'
): Promise<any | null> {
    console.log(`[logStripeEvent] Logging event ${eventId} of type ${type}`)
    const supabase = await createAdminClient()
    
    // Extract customer_id from the data.object if available
    let customerId = null
    if (data.data && data.data.object && 'customer' in data.data.object) {
        customerId = (data.data.object as any).customer
    }
    
    const { data: eventData, error } = await supabase
        .from('stripe_events')
        .insert({
            id: eventId,
            type,
            data, 
            status,
            customer_id: customerId
        })
        .select('*')
        .single()
    
    if (error) {
        console.error('Error logging Stripe event:', error)
        logErrorAsAdmin({
            message: `Error logging Stripe event: ${error.message}`,
            type: 'STRIPE_EVENT_LOGGING_ERROR',
            status: 500,
            stackTrace: error.stack, 
            additionalInfo: error
        })
    }
    
    // When type is 'invoice.paid' and billing reason is 'subscription_update', add a notifications
    if (type === 'invoice.paid') {
        const invoice = data?.data?.object as Stripe.Invoice
        if (invoice?.billing_reason === 'subscription_update') {
            const { error: notificationError } = await supabase
                .from('notifications')
                .insert({
                    message: 'Invoice paid with subscription update',
                    type: 'billing',
                    payload: data,
                    status: 'unresolved'
                })
            if (notificationError) {
                console.error('Error creating notification:', notificationError)
            }
        }
    }
    
    return eventData
}


// Get customers with unprocessed events
export async function getCustomersWithUnprocessedEvents(): Promise<string[]> {
    console.log(`[getCustomersWithUnprocessedEvents] Getting customers with unprocessed events`)
    const supabase = await createAdminClient()
    const { data, error } = await supabase
        .from('stripe_events')
        .select('customer_id')
        .eq('status', 'pending')
        .is('processed_at', null)
    
    if (error) {
        console.error('Error fetching customers with unprocessed events:', error)
        return []
    }
    
    // Extract unique customer IDs using Array.from instead of spread operator
    const customerIds = Array.from(new Set(data.map(event => event.customer_id)))
    return customerIds
}

// Get unprocessed Stripe events for a customer
export async function getUnprocessedStripeEvents(customerId: string): Promise<any[]> {
    console.log(`[getUnprocessedStripeEvents] Getting unprocessed Stripe events for customer ${customerId}`)
    const supabase = await createAdminClient()
    
    let query = supabase
        .from('stripe_events')
        .select('*')
        .eq('status', 'pending')
        .order('data->created', { ascending: true })
    
    // Add customer filter if provided
    if (customerId && customerId.trim() !== '') {
        query = query.eq('customer_id', customerId)
    }
    
    const { data, error } = await query
    
    if (error) {
        console.error('Error fetching unprocessed Stripe events:', error)
        return []
    }
    
    return data || []
}



// Update Stripe event status (replaces markStripeEventProcessing and markStripeEventProcessed)
export async function updateStripeEventStatus(
    eventId: string, 
    status: 'pending' | 'processing' | 'processed' | 'failed'
): Promise<boolean> {
    console.log(`[updateStripeEventStatus] Updating event ${eventId} to status ${status}`)
    const supabase = await createAdminClient()
    
    // Create the update object with just the status
    const updateData: Record<string, any> = { 
        status
    }
    
    const { error } = await supabase
        .from('stripe_events')
        .update(updateData)
        .eq('id', eventId)
    
    if (error) {
        console.error(`Error updating Stripe event status to ${status}:`, error)
        return false
    }
    
    return true
}
